import { useEffect, useRef, useState } from 'react';
import { CameraIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';

import type { IssueComment } from '../../../pages/housekeeping/types/housekeeping';
import { useCommenterPanne } from '../../api/usePannes';
import Button from '../../components/Button';
import Text from '../../components/Text';
import { heurePanne } from '../../helpers/maintenance';
import { t } from '../../i18n/langue';
import ChampTexte from '../menage/ChampTexte';

/**
 * Le fil de commentaires d'une panne : ce qui a été fait, la pièce commandée, la
 * photo du travail fini. Une photo seule suffit (le serveur exige un texte : on
 * pose « Photo jointe »). Pas de push sur les commentaires.
 */
interface FilCommentairesProps {
    issueId: number;
    commentaires: IssueComment[];
}

export default function FilCommentaires({ issueId, commentaires }: FilCommentairesProps) {
    const commenter = useCommenterPanne();
    const refPhoto = useRef<HTMLInputElement>(null);
    const [texte, setTexte] = useState('');
    const [photo, setPhoto] = useState<File | null>(null);
    const [apercu, setApercu] = useState<string | null>(null);

    useEffect(() => () => {
        if (apercu) URL.revokeObjectURL(apercu);
    }, [apercu]);

    const choisirPhoto = (fichier: File | null) => {
        setPhoto(fichier);
        setApercu(fichier ? URL.createObjectURL(fichier) : null);
    };

    const envoyer = () => {
        const contenu = texte.trim() || (photo ? 'Photo jointe' : '');
        if (!contenu || commenter.isLoading) return;
        commenter.mutate(
            { issueId, content: contenu, photo },
            {
                onSuccess: () => {
                    setTexte('');
                    choisirPhoto(null);
                },
            },
        );
    };

    return (
        <section className="flex flex-col gap-3">
            <Text variante="sousTitre" as="h2">
                {t('maintenance.commentaires.titre', { n: commentaires.length })}
            </Text>
            {commentaires.map((c) => (
                <div key={c.id} className="flex flex-col gap-1 rounded-xl bg-white px-3 py-2">
                    <Text variante="petit" ton="attenue">
                        {[c.user_name, heurePanne(c.created_at)].filter(Boolean).join(' · ')}
                    </Text>
                    <Text className="whitespace-pre-line">{c.content}</Text>
                    {(c.photo_urls ?? []).map((url) => (
                        <img key={url} src={url} alt={t('maintenance.commentaires.photoCommentaire')} className="max-h-64 w-full rounded-lg object-contain" />
                    ))}
                </div>
            ))}

            <ChampTexte libelle={t('maintenance.commentaires.ajouter')} valeur={texte} onChange={setTexte} />
            {apercu && (
                <div className="flex items-start gap-2">
                    <img src={apercu} alt={t('maintenance.commentaires.photoAJoindre')} className="h-24 w-24 rounded-lg object-cover" />
                    <Button libelle={t('maintenance.commentaires.retirerPhoto')} icone={XMarkIcon} variante="discret" onPress={() => choisirPhoto(null)} />
                </div>
            )}
            {commenter.isError && (
                <div role="alert">
                    <Text variante="petit" ton="danger">
                        {t('maintenance.commentaires.nonEnvoye')}
                    </Text>
                </div>
            )}
            <div className="flex gap-2">
                <Button
                    libelle={t('maintenance.commentaires.photo')}
                    icone={CameraIcon}
                    variante="secondaire"
                    onPress={() => {
                        if (refPhoto.current) refPhoto.current.value = '';
                        refPhoto.current?.click();
                    }}
                />
                <Button
                    libelle={commenter.isLoading ? t('maintenance.commentaires.envoi') : t('maintenance.commentaires.envoyer')}
                    icone={PaperAirplaneIcon}
                    pleineLargeur
                    disabled={commenter.isLoading || (!texte.trim() && !photo)}
                    onPress={envoyer}
                />
            </div>
            <input
                ref={refPhoto}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid="photo-commentaire"
                onChange={(e) => choisirPhoto(e.target.files?.[0] ?? null)}
            />
        </section>
    );
}
